import { API } from '../apis/apiWrapper';
import { Storage } from '../apis/s3Wrapper';
import { cloneDeep, get } from 'lodash';
import html2canvas from 'html2canvas';
import JSPdf from 'jspdf';

import { fetchAIForm } from 'src/apis';
import { defaultFieldProps, fullFieldProps, usStateOptions } from 'src/constants';
import { FormFieldType, ISummary, TAIForm, TAIFormMaterial, TCustomField, TRequest, IMaterial, TDropItem } from 'src/types';
import { processResponse } from './api';

import { formatDate, getJoinedDate, parseDate } from './day';
import { formatString } from './string';
import { downloadCSV } from './csv';
import { joinIfArray } from './adminUtil';
import { buildBatches } from 'src/services/schoolRequest';

const MARGIN = 10;

export const getUpdatedAIFormValues = (school: any): any => {
  const values = cloneDeep(school);
  const {
    schoolEstMonth,
    schoolEstDay,
    schoolEstYear,
    approvedAccreditation: { cbAccreditedMonth, cbAccreditedDay, cbAccreditedYear },
    administeredTest: { administeredTestMonth, administeredTestDay, administeredTestYear },
  } = school;

  values.schoolEstDate = getJoinedDate(schoolEstMonth, schoolEstDay, schoolEstYear) || parseDate(values.schoolEstDate);

  values.approvedAccreditation.cbAccreditedDate =
    getJoinedDate(cbAccreditedMonth, cbAccreditedDay, cbAccreditedYear) ||
    parseDate(values.approvedAccreditation.cbAccreditedDate);

  values.administeredTest.administeredTestDate =
    getJoinedDate(administeredTestMonth, administeredTestDay, administeredTestYear) ||
    parseDate(values.administeredTest.administeredTestDate || values.administeredTest.administeredtestDate);

  return values;
};

export const getValidatedSchoolName = (schoolName: string): any => {
  //replace greater than symbol ,less than symbol in school name with single space
  const schoolNameWithoutAngleBrackets = schoolName.replace(/[<>]+/g, ' ');

  // for single and double quotes in school name, we need to remove them and NOT replace with single space
  const schoolNameWithoutQuotes = schoolNameWithoutAngleBrackets.replace(/['"]+/g, '');

  return schoolNameWithoutQuotes;
};

export const getAIFormRequestData = (formData: any, userName: string, userEmail: string): any => {
  try {
    const accrExpirationDate = new Date(formData?.approvedAccreditation?.cbAccreditedDate);
    const today = new Date(Date.now());
    const numDaysDiff = Math.ceil((accrExpirationDate.getTime() - today.getTime()) / (1000 * 3600 * 24));
    if (numDaysDiff < 31) {
      formData.accreditationExpiry = 'Y';
    } else {
      formData.accreditationExpiry = 'N';
    }
  } catch (e) {
    formData.accreditationExpiry = 'Y';
  }

  formData.creator = userName;
  formData.creatorEmail = userEmail;

  if (formData.processStatus === 'reopen') {
    formData.processStatus = 'processing';
  } else {
    formData.processStatus = 'submitted';
  }
  return formData;
};

interface ISummaryPK {
  categoryId: string;
}
export const uploadSchoolFile = async (user: string, school: ISummary | TAIForm, file: File, description: string, path?: string) => {
  const contentType = file.type;
  let fileName = file.name;

  if (!contentType || (contentType !== 'application/pdf' && !contentType.startsWith('image'))) {
    throw new Error('You can only upload images or PDF files.');
  }

  fileName = fileName.replace(/[\s,//]+/g, '');
  if (fileName.length > 200) {
    fileName = fileName.substr(0, 200);
  }

  try {
    const result = await Storage.put(school.categoryId + '/support/' + fileName, file, {
      level: 'private',
      contentType: contentType,
    });

    await API.post('AiFormRequest', '/save?key=support', {
      body: {
        ...school,
        user: user,
        s3key: result.key,
        fieldPath: path,
        fileName,
        contentType,
        description,
      },
    }).then((response: any) => processResponse(response));

    return result.key;
  } catch (error: any) {
    throw new Error('Failed to upload file: ' + error.message);
  }
};

export const getFileLink = async (data: IMaterial) => {
  return await API.post('AiFormRequest', '/save?key=getFileLink', {
    body: {
      filePath: data.filePath
    },
  }).then((response: any) => processResponse(response));
};

export const getRuntimeConfigFromSever = async () => {
  return await API.post('AiFormRequest', '/save?key=getRuntimeConfig').then((response: any) => processResponse(response));
};

export const uploadFile = async (user: string, school: ISummaryPK, file: File, description?: string, path?: string, multi?: boolean): Promise<IMaterial> => {
  const contentType = file.type;
  let fileName = file.name;

  if (!contentType || (contentType !== 'application/pdf' && !contentType.startsWith('image'))) {
    throw new Error('You can only upload images or PDF files.');
  }

  fileName = fileName.replace(/[\s,//]+/g, '');
  if (fileName.length > 200) {
    fileName = fileName.substr(0, 200);
  }

  try {
    const result = await Storage.put(school.categoryId + '/support/' + fileName, file, {
      level: 'private',
      contentType: contentType,
    });

    const record: IMaterial = await API.post('AiFormRequest', '/save?key=support', {
      body: {
        ...school,
        user: user,
        s3key: result.key,
        fieldPath: path,
        fileName,
        contentType,
        description,
        multi
      },
    }).then((response: any) => processResponse(response));

    return record;
  } catch (error: any) {
    const type = typeof error;
    let message = 'Unknown error occurred uploading the file';
    if (type === 'object') {
      message = error.message || message;
    } else if (type === 'string') {
      message = error;
    } else {
      console.log(error);
    }
    throw new Error(`Failed to upload file: ${message}`);
  }
};

export const generatePDF = async (printSections: HTMLDivElement[], schoolName: string) => {
  const results = await Promise.all(
    printSections.map(async (section) => {
      const canvas = await html2canvas(section, { logging: false });
      const imgWidth = 210;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      return {
        imageData: canvas,
        width: imgWidth,
        height: imgHeight,
      };
    }),
  );

  const doc = new JSPdf();
  const fileName = schoolName + '.pdf';
  let pageY = MARGIN;

  results.forEach((image) => {
    if (!image.height || !image.width) {
      return;
    }

    const pageHeight = doc.internal.pageSize.getHeight();
    const pageWidth = doc.internal.pageSize.getWidth();

    if (pageY + image.height > pageHeight - MARGIN) {
      pageY = MARGIN;
      if (image.height <= pageHeight) {
        doc.addPage();
      }
    }

    doc.addImage(image.imageData, 'PNG', MARGIN, pageY, pageWidth - 2 * MARGIN, image.height);
    pageY += image.height;
  });

  doc.save(fileName);
};

export const formatProcessStatus = (status: TAIForm['processStatus']) => {
  return {
    started: 'Started',
    submitted: 'Submitted',
    reopen: 'Reopened',
    processing: 'Under Review',
    qcReadyVetting: 'QC Vetting Approved',
    qcReadyReopen: 'QC Reopened',
    qcReadyRejected: 'QC Rejected',
    completed: 'Completed',
    appeal: 'Pending Appeal',
    deactivationApproval: 'Pending Deactivation Approval',
    international: 'Pending International',
    testSecurity: 'Pending Test Security',
    opsApproved: 'Vetting Approved',
    rejected: 'Rejected',
    xpmDenied: 'XPM Denied'
  }[status];
};

function getDate(dateTime:any){
  return `${dateTime.getFullYear()}-${dateTime.getMonth() + 1}-${dateTime.getDate()}`
}

// --- Report types and config ---

export type ReportType = 'applicationStatus' | 'accessCode' | 'codeServices';
export type RecordCategoryType = 'ai' | 'di';

/**
 * Configuration for a single report type.
 *
 * @property headers - Ordered list of CSV column names. Determines column order in output.
 * @property mapper - Transforms a raw API summary object into a key-value record
 *                    where keys MUST match the header names exactly.
 * @property fileName - Downloaded CSV file name (use underscores between words).
 * @property processStatus - Optional. When set, acts as a client-side fallback filter:
 *                           only summaries matching this status are included.
 *                           Backend is expected to handle primary filtering via reportType.
 * @property sortField - Optional. The header column name to sort rows by (newest to oldest).
 *                       Defaults to 'Last Submit Date' if not specified.
 * @property skipStartDate - Optional. When true, startDate is omitted from the API request.
 *                           Used by codeServices reports where backend calculates the start date.
 */
interface ReportConfig {
  headers: string[];
  mapper: (summary: any) => Record<string, any>;
  fileName: string;
  processStatus?: string;
  sortField?: string;
  skipStartDate?: boolean;
}

/**
 * Returns the report configuration (columns, field mappings, file name) for
 * a given report type and record category combination.
 *
 * Report matrix:
 *   - applicationStatus + ai → SC Application Status Report
 *   - applicationStatus + di → SSC Application Status Report
 *   - accessCode + ai       → SC Access Code Report (completed status only)
 *   - accessCode + di       → SSC Access Code Report (completed status only)
 *   - codeServices + ai      → Code Services SC Report (admin year date range)
 *   - codeServices + di      → Code Services SSC Report (admin year date range)
 */
const getReportConfig = (reportType: ReportType, recordCategoryType: RecordCategoryType): ReportConfig => {

  // --- School Code (AI) report configs ---
  if (recordCategoryType === 'ai') {
    if (reportType === 'applicationStatus') {
      return {
        fileName: 'SC_Application_Status_Report.csv',
        headers: [
          'Initial Submit Date',
          'Last Submit Date',
          'Last Update Date',
          'Submitter',
          'Email',
          'School Name',
          'Action',
          'Process Status',
          'CountryIsoCd',
          'City',
          'StateCd',
          'Province',
          'Applied Type',
          'Applied Level',
          'Assigned SC',
          'Assigned AI Level',
          'Interested in Pre-AP Courses',
          'Pre-AP Courses Interested in',
          'Interested in AP Courses',
          'AP Courses Interested in',
          'Interested in SAT Test Center',
          'School Day or Weekend',
          'SAT Admin Date',
          'Interested in PSAT',
        ],
        mapper: (summary) => ({
          'Initial Submit Date': formatDate(summary.submittedDate),
          'Last Submit Date': formatDate(summary.submissionDate),
          'Last Update Date': formatDate(summary.updateDate),
          'Submitter': summary.submitter,
          'Email': summary.email,
          'School Name': summary.schoolName,
          'Action': summary.action,
          'Process Status': formatProcessStatus(summary.processStatus),
          'CountryIsoCd': summary.visitingAddress?.countryIsoCd ?? '',
          'City': summary.visitingAddress?.city ?? '',
          'StateCd': summary.visitingAddress?.stateCd ?? '',
          'Province': summary.visitingAddress?.province ?? '',
          'Applied Type': summary.appliedType ?? '',
          'Applied Level': summary.appliedLevel ?? '',
          'Assigned SC': summary.aiCode ?? '',
          'Assigned AI Level': summary.aiLevel ?? '',
          'Interested in Pre-AP Courses': summary.interestedInPreAPCourses ?? '',
          'Pre-AP Courses Interested in': joinIfArray(summary.preAPCoursesOffered),
          'Interested in AP Courses': summary.apCourseOffering?.apCourseOffer ?? '',
          'AP Courses Interested in': summary.apCourseOffering?.apCourseOfferList ?? '',
          'Interested in SAT Test Center': summary.collegeBoardOfferings?.satTestCenter ?? '',
          'School Day or Weekend': joinIfArray(summary.satAdministrationTypes),
          'SAT Admin Date': summary.collegeBoardOfferings?.satAdminDate ?? '',
          'Interested in PSAT': summary.collegeBoardOfferings?.administerPsat ?? '',
        }),
      };
    }
    if (reportType === 'accessCode') {
      return {
        fileName: 'SC_Access_Code_Report.csv',
        processStatus: 'completed',
        sortField: 'Last Update Date',
        headers: [
          'Last Update Date',
          'School Name',
          'CountryIsoCd',
          'Applied Type',
          'Assigned SC',
          'Assigned AI Level',
          'Principal Name',
          'Principal Email',
          'Principal URL',
          'Interested in AP Courses',
          'Interested in SAT Test Center',
          'Interested in Pre-AP',
          'Interested in PSAT',
        ],
        mapper: (summary) => ({
          'Last Update Date': formatDate(summary.updateDate),
          'School Name': summary.schoolName,
          'CountryIsoCd': summary.visitingAddress?.countryIsoCd ?? '',
          'Applied Type': summary.appliedType ?? '',
          'Assigned SC': summary.aiCode ?? '',
          'Assigned AI Level': summary.aiLevel ?? '',
          'Principal Name': summary.principalName ?? '',
          'Principal Email': summary.principalEmail ?? '',
          'Principal URL': summary.principalWebpage ?? '',
          'Interested in AP Courses': summary.apCourseOffering?.apCourseOffer ?? '',
          'Interested in SAT Test Center': summary.collegeBoardOfferings?.satTestCenter ?? '',
          'Interested in Pre-AP': summary.interestedInPreAPCourses ?? '',
          'Interested in PSAT': summary.collegeBoardOfferings?.administerPsat ?? '',
        }),
      };
    }
  }

  // --- Score Send Code (DI) report configs ---
  if (recordCategoryType === 'di') {
    if (reportType === 'applicationStatus') {
      return {
        fileName: 'SSC_Application_Status_Report.csv',
        headers: [
          'Initial Submit Date',
          'Last Submit Date',
          'Last Update Date',
          'Submitter',
          'Email',
          'Institution Name',
          'Action',
          'Process Status',
          'CountryIsoCd',
          'City',
          'StateCd',
          'Province',
          'Assigned SSC',
          'SAT Program Accepted',
          'AP Program Accepted',
        ],
        mapper: (summary) => ({
          'Initial Submit Date': formatDate(summary.submittedDate),
          'Last Submit Date': formatDate(summary.submissionDate),
          'Last Update Date': formatDate(summary.updateDate),
          'Submitter': summary.submitter,
          'Email': summary.email,
          'Institution Name': summary.schoolName,
          'Action': summary.action,
          'Process Status': formatProcessStatus(summary.processStatus),
          'CountryIsoCd': summary.visitingAddress?.countryIsoCd ?? '',
          'City': summary.visitingAddress?.city ?? '',
          'StateCd': summary.visitingAddress?.stateCd ?? '',
          'Province': summary.visitingAddress?.province ?? '',
          'Assigned SSC': summary.diCode ?? '',
          'SAT Program Accepted': summary.satEligibilityInd ?? '',
          'AP Program Accepted': summary.apEligibilityInd ?? '',
        }),
      };
    }
    if (reportType === 'accessCode') {
      return {
        fileName: 'SSC_Access_Code_Report.csv',
        processStatus: 'completed',
        sortField: 'Last Update Date',
        headers: [
          'Last Update Date',
          'Institution Name',
          'CountryIsoCd',
          'Assigned SSC',
          'SAT Program Accepted',
          'AP Program Accepted',
          'Submitter',
          'Email',
          'SAT Contact Name',
          'SAT Contact Email',
          'AP Contact Name',
          'AP Contact Email',
        ],
        mapper: (summary) => ({
          'Last Update Date': formatDate(summary.updateDate),
          'Institution Name': summary.schoolName,
          'CountryIsoCd': summary.visitingAddress?.countryIsoCd ?? '',
          'Assigned SSC': summary.diCode ?? '',
          'SAT Program Accepted': summary.satEligibilityInd ?? '',
          'AP Program Accepted': summary.apEligibilityInd ?? '',
          'Submitter': summary.submitter,
          'Email': summary.email,
          'SAT Contact Name': summary.satContactName ?? '',
          'SAT Contact Email': summary.satContactEmail ?? '',
          'AP Contact Name': summary.apContactName ?? '',
          'AP Contact Email': summary.apContactEmail ?? '',
        }),
      };
    }
  }

  // --- Code Services report configs ---
  if (reportType === 'codeServices') {
    if (recordCategoryType === 'ai') {
      return {
        fileName: 'Code_Services_SC_Report.csv',
        skipStartDate: true,
        headers: [
          'Initial Submit Date',
          'Last Submit Date',
          'Last Update Date',
          'School Name',
          'Action',
          'Process Status',
          'CountryIsoCd',
          'Applied Type',
          'Applied Level',
          'Assigned SC',
          'Assigned AI Level',
          'NMI',
          'XPM Denied',
          'Days In Current Status',
          'Days Since Initial Submit',
          'Days Since Last Submit',
        ],
        mapper: (summary) => ({
          'Initial Submit Date': formatDate(summary.submittedDate),
          'Last Submit Date': formatDate(summary.submissionDate),
          'Last Update Date': formatDate(summary.updateDate),
          'School Name': summary.schoolName,
          'Action': summary.action,
          'Process Status': formatProcessStatus(summary.processStatus),
          'CountryIsoCd': summary.visitingAddress?.countryIsoCd ?? '',
          'Applied Type': summary.appliedType ?? '',
          'Applied Level': summary.appliedLevel ?? '',
          'Assigned SC': summary.aiCode ?? '',
          'Assigned AI Level': summary.aiLevel ?? '',
          'NMI': summary.reOpen ?? '',
          'XPM Denied': summary.xpmDenied ?? '',
          'Days In Current Status': summary.daysInCurrentStatus ?? '',
          'Days Since Initial Submit': summary.daysSinceInitialSubmit ?? '',
          'Days Since Last Submit': summary.daysSinceLastSubmit ?? '',
        }),
      };
    }
    if (recordCategoryType === 'di') {
      return {
        fileName: 'Code_Services_SSC_Report.csv',
        skipStartDate: true,
        headers: [
          'Initial Submit Date',
          'Last Submit Date',
          'Last Update Date',
          'Institution Name',
          'Action',
          'Process Status',
          'CountryIsoCd',
          'Assigned SSC',
          'NMI',
          'XPM Denied',
          'Days In Current Status',
          'Days Since Initial Submit',
          'Days Since Last Submit',
        ],
        mapper: (summary) => ({
          'Initial Submit Date': formatDate(summary.submittedDate),
          'Last Submit Date': formatDate(summary.submissionDate),
          'Last Update Date': formatDate(summary.updateDate),
          'Institution Name': summary.schoolName,
          'Action': summary.action,
          'Process Status': formatProcessStatus(summary.processStatus),
          'CountryIsoCd': summary.visitingAddress?.countryIsoCd ?? '',
          'Assigned SSC': summary.diCode ?? '',
          'NMI': summary.reOpen ?? '',
          'XPM Denied': summary.xpmDenied ?? '',
          'Days In Current Status': summary.daysInCurrentStatus ?? '',
          'Days Since Initial Submit': summary.daysSinceInitialSubmit ?? '',
          'Days Since Last Submit': summary.daysSinceLastSubmit ?? '',
        }),
      };
    }
  }

  throw new Error(`Report configuration not found for reportType='${reportType}', recordCategoryType='${recordCategoryType}'.`);
};

/**
 * Generic report builder. Fetches summaries from the backend and generates a CSV download.
 * Passes reportType to the backend API. Backend determines filtering based on reportType.
 */
export const buildReport = async (reportType: ReportType, recordCategoryType: RecordCategoryType) => {
  const config = getReportConfig(reportType, recordCategoryType);

  const today = new Date();
  today.setDate(today.getDate() + 1); // next day to include today's records

  const lastYear = new Date();
  lastYear.setFullYear(lastYear.getFullYear() - 1);

  console.debug('buildReport: reportType=' + reportType + ', recordCategoryType=' + recordCategoryType
    + ', startDate=' + getDate(lastYear) + ', endDate=' + getDate(today));

  // Build request body — backend uses reportType to determine filtering logic
  const requestBody: any = {
    recordCategoryType,
    reportType,
    endDate: getDate(today),
  };
  // startDate is omitted for codeServices — backend calculates it based on admin year
  if (!config.skipStartDate) {
    requestBody.startDate = getDate(lastYear);
  }

  const summaries: any = await API.post('AiFormRequest', '/save?key=retrieveSubmissionRequests', {
    body: requestBody,
  }).then((response: any) => processResponse(response));

  // Map, filter, sort
  let mapped = summaries
    .map((summary: any) => {
      // DEX Requests do not have submittedDate — skip them
      if (!summary.submittedDate) return null;
      // Client-side fallback: filter by processStatus in case backend doesn't support it yet
      if (config.processStatus && summary.processStatus !== config.processStatus) return null;
      return config.mapper(summary);
    })
    .filter((each: any) => each);

  // Sort by the configured sort field (defaults to 'Last Submit Date'), newest to oldest
  const sortField = config.sortField ?? 'Last Submit Date';
  mapped.sort((a: any, b: any) => {
    const aDate = new Date(a[sortField] ?? '');
    const bDate = new Date(b[sortField] ?? '');
    return bDate.getTime() - aDate.getTime();
  });

  const csvData = mapped
    .map((row: any) =>
      config.headers
        .map((fieldName) => {
          const value: string = get(row, fieldName, '');
          if (typeof value === 'string') {
            return formatString(value);
          } else if (typeof value === 'number') {
            return value;
          } else {
            return '';
          }
        })
        .join(','),
    )
    .join('\n');

  downloadCSV({
    headers: config.headers,
    data: csvData,
    fileName: config.fileName,
  });
};

export const getAddressFields = (prefix: string, countries: TDropItem[]): TCustomField[] => {
  return [
    {
      label: 'Address 1:',
      comment: 'P.O. Box addresses are not acceptable',
      path: `${prefix}.address1`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      required: true,
      ...fullFieldProps,
    },
    {
      label: 'Address 2:',
      path: `${prefix}.address2`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      ...fullFieldProps,
    },
    {
      label: 'Address 3:',
      path: `${prefix}.address3`,
      placeholder: 'Street Address',
      type: FormFieldType.TEXT,
      ...fullFieldProps,
    },
    {
      label: 'City:',
      path: `${prefix}.city`,
      placeholder: 'City',
      type: FormFieldType.TEXT,
      required: true,
      ...defaultFieldProps,
    },
    {
      label: 'County:',
      path: `${prefix}.county`,
      placeholder: 'County',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'State/Province:',
      path: `${prefix}.stateOrProvince`,
      placeholder: 'State/Province',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country !== 'US' && field[prefix]?.country !== '',
    },
    {
      label: 'Country:',
      path: `${prefix}.country`,
      placeholder: 'Select One',
      type: FormFieldType.SELECT,
      options: countries,
      required: true,
      ...defaultFieldProps,
    },
    {
      label: 'State:',
      path: `${prefix}.state`,
      placeholder: 'Select One',
      type: FormFieldType.SELECT,
      options: usStateOptions,
      ...defaultFieldProps,
      required: true,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'Zip:',
      maxLength: 5,
      path: `${prefix}.zip`,
      placeholder: '5 Digit Zip Code',
      type: FormFieldType.TEXT,
      required: true,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country === 'US' || field[prefix]?.country === '',
    },
    {
      label: 'International Postal Code:',
      path: `${prefix}.internationalPostalCode`,
      placeholder: 'International Postal Code',
      type: FormFieldType.TEXT,
      ...defaultFieldProps,
      renderCondition: (field) => field[prefix]?.country !== 'US' && field[prefix]?.country !== '',
    },
  ];
};
